import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { mintOrder } from './territory';
import type { Building, Cell, EntityId, PlayerId, ResourceId, SimState } from './types';

/**
 * Resources live in cities: each completed city (a building with a
 * `territoryRadius`) keeps its own `stock`. A player's resources are the sum
 * over their cities, so losing a city loses what it held. Every amount here
 * (stock, costs, deposits) is in thousandths (fixed.ts): pass a rules cost
 * through `milliCost` first.
 */

/** Whether `b` can hold stock: a completed building with a territory (a city). */
function holdsStock(rules: RuleSet, b: Building): boolean {
  return b.buildProgress >= 1 && rules.buildings[b.type]?.territoryRadius !== undefined;
}

/** `owner`'s completed cities, oldest first. */
function stockCities(state: SimState, rules: RuleSet, owner: PlayerId): Building[] {
  return Object.values(state.buildings)
    .filter((b) => b.owner === owner && holdsStock(rules, b))
    .sort((a, b) => mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1));
}

/** What `owner` holds in total: the sum of their cities' stock. */
function playerStock(state: SimState, owner: PlayerId): Record<ResourceId, number> {
  const total: Record<ResourceId, number> = {};
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== owner || !b.stock) continue;
    for (const [res, amt] of Object.entries(b.stock)) total[res] = (total[res] ?? 0) + amt;
  }
  return total;
}

function canAfford(state: SimState, owner: PlayerId, cost: Record<ResourceId, number>): boolean {
  const stock = playerStock(state, owner);
  for (const [res, amt] of Object.entries(cost)) {
    if ((stock[res] ?? 0) < amt) return false;
  }
  return true;
}

/**
 * Pays `cost` from `owner`'s cities: each resource comes from the city holding
 * the most of it, then the next most, and so on (ties to the older city).
 * Callers check `canAfford` first; a shortfall just empties what there is.
 */
function spend(state: SimState, owner: PlayerId, cost: Record<ResourceId, number>): void {
  const cities = Object.values(state.buildings).filter((b) => b.owner === owner && b.stock);
  for (const [res, amt] of Object.entries(cost)) {
    let left = amt;
    const order = [...cities].sort(
      (a, b) =>
        (b.stock?.[res] ?? 0) - (a.stock?.[res] ?? 0) ||
        mintOrder(a.id) - mintOrder(b.id) ||
        (a.id < b.id ? -1 : 1)
    );
    for (const city of order) {
      if (left <= 0) break;
      const stock = city.stock;
      const have = stock?.[res] ?? 0;
      if (!stock || have <= 0) break;
      const take = Math.min(have, left);
      stock[res] = have - take;
      left -= take;
    }
  }
}

/** Adds `amount` of `res` to city `cityId`'s stock. */
function deposit(state: SimState, cityId: EntityId, res: ResourceId, amount: number): void {
  const city = state.buildings[cityId];
  if (!city || amount <= 0) return;
  const stock = city.stock ?? {};
  stock[res] = (stock[res] ?? 0) + amount;
  city.stock = stock;
}

/** `owner`'s completed city nearest `pos` (ties to the older city), if any. */
function nearestStockCity(
  state: SimState,
  rules: RuleSet,
  owner: PlayerId,
  pos: Cell
): Building | undefined {
  let best: Building | undefined;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const city of stockCities(state, rules, owner)) {
    const d = axialHexDistance(city.pos, pos);
    if (d < bestDist) {
      bestDist = d;
      best = city;
    }
  }
  return best;
}

/**
 * The city that receives what `owner` gathers at `pos`: the city whose
 * territory holds the tile when it's theirs, else their nearest completed
 * city. Undefined when they have no completed city (the gain is lost).
 */
function gatherDepositCity(
  state: SimState,
  rules: RuleSet,
  owner: PlayerId,
  pos: Cell,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): EntityId | undefined {
  const territoryCity = state.buildings[owners.get(getHexTileMapKey(pos)) ?? ''];
  if (territoryCity && territoryCity.owner === owner && holdsStock(rules, territoryCity)) {
    return territoryCity.id;
  }
  return nearestStockCity(state, rules, owner, pos)?.id;
}

/**
 * Where plunder of `res` taken at `pos` goes: `owner`'s city holding the most
 * of it, ties to the city nearest `pos`, then the older one.
 */
function plunderDepositCity(
  state: SimState,
  rules: RuleSet,
  owner: PlayerId,
  res: ResourceId,
  pos: Cell
): EntityId | undefined {
  let best: Building | undefined;
  let bestAmt = Number.NEGATIVE_INFINITY;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const city of stockCities(state, rules, owner)) {
    const amt = city.stock?.[res] ?? 0;
    const d = axialHexDistance(city.pos, pos);
    if (amt > bestAmt || (amt === bestAmt && d < bestDist)) {
      best = city;
      bestAmt = amt;
      bestDist = d;
    }
  }
  return best?.id;
}

export {
  canAfford,
  deposit,
  gatherDepositCity,
  holdsStock,
  nearestStockCity,
  playerStock,
  plunderDepositCity,
  spend,
  stockCities,
};
